<template>
  <div class="sp-finder"><slot /></div>
</template>

<script setup>
import { inject, onScopeDispose } from "vue";
import { useLegacyHost } from "#ce/apps/shared/composables/useLegacyHost";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { getFinderKind } from "#ce/apps/SpFinder/kinds";
import { createFinderStore } from "#ce/apps/SpFinder/store/finder-store";
import { useFinderHold } from "#ce/apps/SpFinder/composables/useFinderHold";
import { props as finderProps } from "./props";

const props = defineProps(finderProps);
const kindDefinition = getFinderKind(props.kind);

const emit = defineEmits(["search-change"]);
const host = useHostElement();

function reportSearch(snapshot) {
  emit("search-change", snapshot);
}

const createLegacyAdapter = inject("createFinderLegacyAdapter");
const legacy = createLegacyAdapter();
const finder = createFinderStore(reportSearch, { commands: legacy.commands, kind: kindDefinition });
onScopeDispose(legacy.register(host, finder));
useFinderHold(host, finder);
const { exposeProperties, exposeMethods } = useExpose();
const bootFinderEvents = inject("bootFinderEvents");

function bootEvents(options) {
  if (props.kind !== "events") {
    throw new Error("Events boot requires kind=events");
  }
  return bootFinderEvents(host, options);
}

exposeProperties({
  kindDefinition: {
    get: () => kindDefinition,
  },
  state: {
    get: () => finder.state.value,
  },
  pendingRelease: {
    set: legacy.queueRelease,
  },
  fetchingHeld: {
    get: () => finder.fetchingHeld,
    set: (value) => {
      finder.fetchingHeld = value;
    },
  },
});
exposeMethods({
  bootEvents,
  search: legacy.commands.search,
  subscribe: finder.subscribe,
  updateLandingDraft: finder.updateLandingDraft,
  submitLandingSearch: finder.submitLandingSearch,
  navigate: finder.navigate,
  holdFetching: finder.holdFetching,
  releaseFetching: finder.releaseFetching,
  applyRequiredFilters: finder.applyRequiredFilters,
  persistSettings: finder.persistSettings,
  ...finder.resultCommands,
  ...finder.mapCommands,
});

const loadDoctorFinder = inject("loadFinderLegacy");

function bootLegacyFinder({ bootDoctorFinder }, { host: legacyHost }) {
  return bootDoctorFinder(props.boot, legacyHost, { locale: props.locale });
}

if (props.boot) {
  useLegacyHost({ load: loadDoctorFinder, boot: bootLegacyFinder });
}
</script>

<style>
:host {
  display: block;
}
</style>
